import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';

const FOCUSABLE = 'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Generalized modal shell (backdrop + card + optional header/close + body + footer slots).
 * Extracted from ConfirmModal so the hand-rolled dialogs (e.g. PermissionEditor)
 * can reuse it. Footer is a slot so each caller controls its own buttons.
 *
 * Accessibility: focuses the panel on open, traps Tab within it, and closes on Escape.
 *
 * Props: isOpen, onClose, title, icon (lucide), iconClassName, children (body),
 *        footer (node), maxWidth ('max-w-md' | 'max-w-lg' | ...). Returns null when closed.
 */
const Modal = ({ isOpen, onClose, title, icon: Icon, iconClassName = 'text-gray-500', children, footer, maxWidth = 'max-w-md' }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    // Move focus into the dialog so screen-reader/keyboard users land inside.
    panel.focus();

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = panel.querySelectorAll(FOCUSABLE);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fadeIn">
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`bg-surface rounded-lg shadow-xl w-full ${maxWidth} animate-slideIn outline-none`}
      >
        {(title || Icon) && (
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {Icon && <Icon className={`h-6 w-6 ${iconClassName}`} />}
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {children && <div className="p-6">{children}</div>}

        {footer && (
          <div className="flex items-center justify-end space-x-3 p-6 bg-gray-50 rounded-b-lg">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
